import React from 'react';
import { Box, Typography, Button, Alert, AlertTitle } from '@mui/material';
import { Warning, Error, Info } from '@mui/icons-material';
import { PaymentMethodStatusData } from '../types/chargebee';

interface PaymentWarningProps {
  paymentStatus: PaymentMethodStatusData;
  onUpdatePayment: () => void;
  onDismiss?: () => void;
}

const PaymentWarning: React.FC<PaymentWarningProps> = ({ 
  paymentStatus, 
  onUpdatePayment, 
  onDismiss 
}) => {
  const getWarningConfig = () => {
    // Priority order: expired > invalid > expiring > no payment method
    if (paymentStatus.hasExpiredCard) {
      return {
        severity: 'error' as const,
        icon: <Error />,
        title: 'Payment Method Expired',
        message: `Your ${paymentStatus.primaryPaymentMethod.card.brand.toUpperCase()} card ending in ${paymentStatus.primaryPaymentMethod.card.last4} has expired. Please update your payment method to continue using our services.`,
        actionText: 'Update Payment Method',
        color: '#d32f2f'
      };
    }

    if (paymentStatus.hasInvalidCard) {
      return {
        severity: 'error' as const,
        icon: <Error />,
        title: 'Invalid Payment Method',
        message: `Your ${paymentStatus.primaryPaymentMethod.card.brand.toUpperCase()} card ending in ${paymentStatus.primaryPaymentMethod.card.last4} is invalid. Please update your payment method to continue using our services.`,
        actionText: 'Update Payment Method',
        color: '#d32f2f'
      };
    }

    if (paymentStatus.hasExpiringCard) {
      return {
        severity: 'warning' as const,
        icon: <Warning />,
        title: 'Payment Method Expiring Soon',
        message: `Your ${paymentStatus.primaryPaymentMethod.card.brand.toUpperCase()} card ending in ${paymentStatus.primaryPaymentMethod.card.last4} expires in ${paymentStatus.primaryPaymentMethod.card.expiryMonth}/${paymentStatus.primaryPaymentMethod.card.expiryYear}. Please update your payment method to avoid service interruption.`,
        actionText: 'Update Payment Method',
        color: '#ed6c02'
      };
    }

    if (!paymentStatus.hasPaymentMethod) {
      return {
        severity: 'warning' as const,
        icon: <Info />,
        title: 'No Payment Method Found',
        message: 'You don\'t have a payment method on file. Please add a payment method to ensure uninterrupted service.',
        actionText: 'Add Payment Method',
        color: '#ed6c02'
      };
    }

    return null;
  };

  const warningConfig = getWarningConfig();

  if (!warningConfig) {
    return null; // No warning needed
  }

  return (
    <Box sx={{ mb: 3 }}>
      <Alert
        severity={warningConfig.severity}
        icon={warningConfig.icon}
        sx={{
          borderRadius: '12px',
          boxShadow: '0px 4px 12px 0px rgba(43, 43, 48, 0.06)',
          border: `1px solid ${warningConfig.color}20`,
          '& .MuiAlert-icon': {
            color: warningConfig.color,
            fontSize: '24px'
          },
          '& .MuiAlert-message': {
            width: '100%'
          }
        }}
        action={
          onDismiss && (
            <Button
              color="inherit"
              size="small"
              onClick={onDismiss}
              sx={{ 
                color: warningConfig.color,
                fontWeight: 500,
                textTransform: 'none'
              }}
            >
              Dismiss
            </Button>
          )
        }
      >
        <AlertTitle sx={{ 
          color: warningConfig.color, 
          fontWeight: 600,
          fontSize: '16px',
          mb: 1
        }}>
          {warningConfig.title}
        </AlertTitle>
        <Typography 
          variant="body2" 
          sx={{ 
            color: '#666666',
            fontSize: '14px',
            lineHeight: 1.5,
            mb: 2
          }}
        >
          {warningConfig.message}
        </Typography>
        <Button
          variant="contained"
          size="small"
          onClick={onUpdatePayment}
          sx={{
            backgroundColor: warningConfig.color,
            color: '#fff',
            fontWeight: 500,
            textTransform: 'none',
            borderRadius: '6px',
            px: 3,
            py: 1,
            '&:hover': {
              backgroundColor: warningConfig.color,
              opacity: 0.9
            }
          }}
        >
          {warningConfig.actionText}
        </Button>
      </Alert>
    </Box>
  );
};

export default PaymentWarning;
